export const agentControlNodes = [
  {
    id: "core",
    name: "Agent core",
    function: "Owns the objective, routes every engine surface, reconciles the graph, and returns the final memo.",
  },
  {
    id: "sources",
    name: "Sources",
    function: "Retrieves claims, observations, people, institutions, media, and market-source evidence.",
  },
  {
    id: "systems",
    name: "Systems",
    function: "Maps mechanisms, incentives, dependencies, constraints, feedback, and regime state.",
  },
  {
    id: "timeline",
    name: "Timeline",
    function: "Connects observed events, claim deadlines, projections, branches, and verification windows.",
  },
  {
    id: "simulation",
    name: "Simulation",
    function: "Tests mechanisms and trajectories against registered simulation models and limitations.",
  },
  {
    id: "portfolio",
    name: "Portfolio",
    function: "Maps exposures and research-only expressions without changing capital or placing orders.",
  },
  {
    id: "skeptic",
    name: "Skeptic",
    function: "Attacks the cross-domain evidence chain, alternatives, assumptions, and false precision.",
  },
  {
    id: "evaluator",
    name: "Evaluator",
    function: "Scores the integrated run and may propose one bounded control-parameter change for owner review.",
  },
] as const;

export type AgentControlNodeId = (typeof agentControlNodes)[number]["id"];

export const agentControlEdges = [
  { from: "core", to: "sources", mode: "delegates" },
  { from: "sources", to: "core", mode: "returns" },
  { from: "core", to: "systems", mode: "delegates" },
  { from: "systems", to: "core", mode: "returns" },
  { from: "core", to: "timeline", mode: "delegates" },
  { from: "timeline", to: "core", mode: "returns" },
  { from: "core", to: "simulation", mode: "delegates" },
  { from: "simulation", to: "core", mode: "returns" },
  { from: "core", to: "portfolio", mode: "delegates" },
  { from: "portfolio", to: "core", mode: "returns" },
  { from: "core", to: "skeptic", mode: "delegates" },
  { from: "skeptic", to: "core", mode: "returns" },
  { from: "core", to: "evaluator", mode: "required" },
  { from: "evaluator", to: "core", mode: "conditional revision" },
] as const satisfies ReadonlyArray<{
  from: AgentControlNodeId;
  to: AgentControlNodeId;
  mode: "delegates" | "returns" | "required" | "conditional revision";
}>;

export const agentDomainSurfaces = agentControlNodes.filter((node) =>
  ["sources", "systems", "timeline", "simulation", "portfolio"].includes(node.id),
) as ReadonlyArray<(typeof agentControlNodes)[1 | 2 | 3 | 4 | 5]>;

export type AgentDomainId = (typeof agentDomainSurfaces)[number]["id"];

export const agentControlAutonomy = {
  allowed: [
    "Read registered Asymmetric records",
    "Route analysis across Sources, Systems, Timeline, Simulation, and Portfolio",
    "Coordinate OpenAI specialist agents and parallelize read-only work",
    "Challenge assumptions and revise a memo",
    "Score a completed run",
    "Propose bounded control changes",
  ],
  prohibited: [
    "Read or reveal credentials",
    "Place, cancel, or size live or paper orders",
    "Modify portfolio records",
    "Change IAM, infrastructure, or deployments",
    "Message people or publish externally",
    "Apply its own improvement proposals",
  ],
} as const;

export type AgentControlConfig = {
  version: number;
  evidenceMinimum: number;
  revisionThreshold: number;
  maxRevisions: number;
  skepticPasses: number;
  updatedAt: string;
};

export const defaultAgentControlConfig: AgentControlConfig = {
  version: 1,
  evidenceMinimum: 4,
  revisionThreshold: 72,
  maxRevisions: 1,
  skepticPasses: 1,
  updatedAt: "",
};

export const agentControlParameterBounds = {
  evidenceMinimum: { minimum: 2, maximum: 12, label: "Evidence minimum" },
  revisionThreshold: { minimum: 50, maximum: 90, label: "Revision threshold" },
  maxRevisions: { minimum: 0, maximum: 2, label: "Maximum revisions" },
  skepticPasses: { minimum: 1, maximum: 2, label: "Skeptic passes" },
} as const;

export type AgentControlParameter = keyof typeof agentControlParameterBounds;

export function isAgentControlParameter(value: string): value is AgentControlParameter {
  return Object.hasOwn(agentControlParameterBounds, value);
}

export function normalizeAgentControlParameter(
  parameter: AgentControlParameter,
  value: number,
) {
  const bounds = agentControlParameterBounds[parameter];
  return Math.min(bounds.maximum, Math.max(bounds.minimum, Math.round(value)));
}

export type ControllerOutput = {
  question: string;
  subquestions: string[];
  requiredEvidence: string[];
  systemFocus: string[];
  terminationCondition: string;
  domainRoute?: Array<{
    domain: AgentDomainId;
    priority: "primary" | "supporting";
    task: string;
  }>;
};

export type AgentDomainContribution = {
  id: AgentDomainId;
  status: "consulted" | "insufficient" | "not_consulted";
  summary: string;
  evidence: ScoutEvidence[];
  dependencies: AgentDomainId[];
  gaps: string[];
  confidence: number;
};

export type ScoutEvidence = {
  id: string;
  kind: "source" | "signal" | "claim" | "system" | "simulation" | "projection" | "timeline" | "portfolio";
  title: string;
  observation: string;
  relevance: string;
  support: "direct" | "derived" | "hypothesis";
};

export type ScoutOutput = {
  evidence: ScoutEvidence[];
  gaps: string[];
};

export type ModelerOutput = {
  model: string;
  mechanisms: string[];
  scenarios: string[];
  assumptions: string[];
  confidence: number;
};

export type SkepticOutput = {
  verdict: "pass" | "revise" | "insufficient";
  contradictions: string[];
  alternativeExplanations: string[];
  missingEvidence: string[];
  confidencePenalty: number;
};

export type CandidateAction = {
  label: string;
  vehicle: string;
  direction: "observe" | "buy" | "sell" | "hedge" | "avoid";
  rationale: string;
  condition: string;
  invalidation: string;
  allocation: "0 — research only";
};

export type SynthesizerOutput = {
  conclusion: string;
  confidence: number;
  keyReasons: string[];
  unknowns: string[];
  candidateActions: CandidateAction[];
};

export type EvaluatorOutput = {
  score: number;
  criteria: {
    evidence: number;
    mechanism: number;
    adversarial: number;
    actionability: number;
    calibration: number;
  };
  feedback: string[];
  shouldPropose: boolean;
  proposal: {
    parameter: AgentControlParameter;
    proposedValue: number;
    rationale: string;
    expectedEffect: string;
    confidence: number;
  };
};

export type AgentStageTrace = {
  nodeId: AgentControlNodeId;
  pass: number;
  status: "complete" | "failed";
  startedAt: string;
  completedAt: string;
  durationMs: number;
};

export type AgentControlRunResult = {
  runtime?: "openai-agents-sdk";
  domains?: AgentDomainContribution[];
  controller: ControllerOutput;
  scout: ScoutOutput;
  modeler: ModelerOutput;
  skeptic: SkepticOutput[];
  synthesizer: SynthesizerOutput;
  evaluator: EvaluatorOutput;
  revisions: number;
  trace: AgentStageTrace[];
};

export type AgentControlRun = {
  id: string;
  objective: string;
  status: "running" | "completed" | "failed";
  graphVersion: number;
  config: AgentControlConfig;
  result: AgentControlRunResult | null;
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
};

export type AgentControlProposal = {
  id: string;
  runId: string;
  status: "proposed" | "applied" | "rejected";
  parameter: AgentControlParameter;
  currentValue: number;
  proposedValue: number;
  rationale: string;
  expectedEffect: string;
  confidence: number;
  createdAt: string;
  decidedAt: string | null;
};

export type AgentControlState = {
  configured: boolean;
  graph: {
    config: AgentControlConfig;
    nodes: typeof agentControlNodes;
    edges: typeof agentControlEdges;
    autonomy: typeof agentControlAutonomy;
  };
  runs: AgentControlRun[];
  proposals: AgentControlProposal[];
};
